import { api } from '../../shared/api/client'
import { requireMarketplace, type MarketplaceCode } from '../../shared/marketplaces'

/**
 * Каталог «Карточки МП» (черновики PIM) знает площадки с выгрузкой —
 * `capabilities.publish = true`: ВБ, ОЗОН и, с Этапа 2
 * (`docs/SPEC_2026-09-13_YANDEX_MARKET.md`, §4.2), Я.Маркет. Сужаем
 * `MarketplaceCode` реестра до них, а не заводим отдельный union заново.
 */
export type PimMarketplace = Extract<MarketplaceCode, 'wb' | 'ozon' | 'ym'>

export interface PimMarketplaceCard {
  id: number
  article: string
  design_code: string | null
  marketplace: PimMarketplace
  seller_id: number
  seller_name: string | null
  seller_short_name: string | null
  cabinet: string | null
  external_id: string | null
  /** Ключ склейки «тематика + категория», например T1441:C1. Null у исторических карточек. */
  merge_key: string | null
  /** Человекочитаемое имя склейки: «Первое сентября — 1441 / PT1». */
  merge_label: string | null
  /** Склейка, в которой карточка состоит по замыслу; по ней открывается мастер. */
  bundle_id?: number | null
  /** Карточка WB (imtID), в которую вошла номенклатура. Null — ещё не склеена. */
  imt_id: number | null
  status: string
  /**
   * Явное состояние отправки — по нему сервер решает, можно ли отправлять.
   *
   * `not_sent` наружу не ходили, `in_flight` запрос в полёте, `sent` ушёл без
   * ответа, `rejected` площадка отказала, `confirmed` карточка на площадке.
   * `status` остаётся витриной жизненного цикла и права на отправку не даёт:
   * «Ошибка» бывает и до отправки, и после неё.
   *
   * Необязательное: живой ответ поле отдаёт всегда, но отсутствие значения
   * должно переводить отбор на прежнюю эвристику по `status`, а не закрывать
   * выгрузку целиком.
   */
  submit_state?: string | null
  publication_mode: 'draft' | 'publish'
  price: number | null
  stock: number | null
  sales_enabled_at?: string | null
  published_at?: string | null
  publish_excluded_at?: string | null
  publish_excluded_by?: number | null
  publish_excluded_reason?: string | null
  error: string | null
  /**
   * Код последнего отказа — префикс `CODE:` из текста ошибки, иначе
   * `MARKETPLACE_PUBLISH_FAILED`. Бэкенд отдаёт его в составе `mc.*` всегда;
   * вкладка «Черновики PIM» называет по нему причину человеческими словами.
   */
  last_error_code?: string | null
  created_at: string
  updated_at: string
  title: string | null
  /** Наименование для площадки из SEO — то, что уедет на МП. */
  seo_title: string | null
  category_id: number | null
  category_prefix: string | null
  category_description: string | null
  design_type: string | null
  design_category: string | null
  /** Бренд макета (base_designs.brand_ru) — по нему группируется вид «По брендам». */
  design_brand?: string | null
  design_theme: string | null
  /**
   * Номер тематики макета — по нему группируется вид «По тематикам». Текст
   * `design_theme` для этого не годится: одно название бывает у разных тематик.
   */
  design_theme_number?: number | null
  /** Название тематики из реестра (design_themes.name), обычно «Динозавры — 1461». */
  design_theme_name?: string | null
  design_legal_status: string | null
  has_print_file: boolean
  preview_url: string | null
  /**
   * Тот же кадр путём в дереве воронок — по нему наш маршрут отдаёт миниатюру.
   * Пусто, если кадр не с хоста визуалов (локальный путь, чужой адрес).
   */
  preview_path?: string | null
  image_urls: string[]
}

export interface PimMarketplaceCardsPage {
  items: PimMarketplaceCard[]
  total: number
  limit: number
  offset: number
  /** false — счётчики в ответе пустые, потому что их не просили */
  facets_included: boolean
  counts: {
    by_status: Record<string, number>
    by_category: Record<string, number>
    by_publication_mode: Record<string, number>
  }
  category_facets: PimMarketplaceCategoryFacet[]
  /** Только в режиме articles_resolve — подмножество переданных значений, которым нашлась карточка */
  articles_matched?: string[]
}

export interface PimMarketplaceCategoryFacet {
  id: number
  label: string
  description: string | null
  count: number
}

export interface PimPayloadPreviewError {
  code: string
  message: string
}

export interface PimPayloadPreviewSource {
  card_revision: number
  run_id: string | null
  seo_content_id: number | null
  seo_revision_id: number | null
  seo_revision: number | null
  frozen_at: string | null
}

export interface PimMarketplacePayloadPreview {
  card_id: number
  article: string
  marketplace: PimMarketplace
  seller_id: number
  exact: boolean
  source: PimPayloadPreviewSource | null
  /** Финальный item OZON либо тела шагов card_upload/media_upload для WB. */
  payload: Record<string, any> | null
  media_urls: string[]
  validation_errors: PimPayloadPreviewError[]
  /**
   * Имена характеристик: идентификатор → подпись из справочника площадки.
   * Само тело отправки остаётся с числами, как того требует площадка.
   */
  characteristic_labels: Record<string, string>
  /** Откуда пришло значение: резолвится на бэке по mp_characteristic_levels. */
  characteristic_sources?: Record<string, 'seo' | 'category' | 'product'>
  /** Секция четвёртого этажа; не зависит от локализованной подписи. */
  characteristic_sections?: Record<string, string>
  /** Имя предмета WB или категории Ozon: в теле стоит номер, человеку нужен текст. */
  category_name: string | null
  /**
   * Ручные правки состава. В теле отправки они уже учтены — нужны, чтобы
   * показать, какие поля изменены руками, и дать их снять.
   */
  overrides: PimCardOverrides | null
}

/** Ручные правки состава карточки перед выгрузкой. */
export interface PimCardOverrides {
  title?: string
  description?: string
  price?: number
  dimensions?: Record<string, number>
  characteristics?: Record<string, unknown>
  media_urls?: string[]
}

export interface PimMarketplaceCardsFilters {
  marketplace: PimMarketplace
  id?: number
  status?: string
  search?: string
  design_code?: string
  category_id?: number
  publication_mode?: 'draft' | 'publish'
  /** Только карточки без внешнего ID — реестр «ещё не на площадке». */
  not_published?: boolean
  publish_excluded?: boolean
  has_error?: boolean
  dateFrom?: string
  dateTo?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
  /**
   * 0 — не считать счётчики и фасеты выборки. Догрузка следующих порций читает
   * их с первой порции, а на большом кабинете четыре агрегата стоят дороже
   * самой страницы.
   */
  facets?: 0 | 1
  /** CSV значений для отбора по списку артикулов: сопоставляется по всем идентификаторам строки */
  articles?: string
  /**
   * Режим проверки списка артикулов: '1'. Прочие фильтры (кроме кабинета)
   * игнорируются, в ответе только `articles_matched`.
   */
  articles_resolve?: string
}

/** Причина, по которой сервер не смог удалить черновик. */
export type PimMarketplaceDeleteBlockedReason = 'published' | 'in_flight' | 'not_draft' | 'not_found'

export interface PimMarketplaceDeleteBlocked {
  card_id: number
  reason: PimMarketplaceDeleteBlockedReason
}

export interface PimMarketplaceDeleteResult {
  deleted: number[]
  blocked: PimMarketplaceDeleteBlocked[]
  bundles_removed: number[]
  revisions_removed: number
  outbox_removed: number
  /** Сколько строк product_artifacts (визуалы кабинета) снято у удалённых карточек */
  visual_rows_removed: number
  /** Сколько файлов реально удалено на диске */
  visual_files_deleted: number
  /** Технические сообщения об отказах уборки файлов/недоступности media-generation-service */
  warnings: string[]
}

const unwrap = (response: any): PimMarketplaceCardsPage => {
  const data = response?.data?.data ?? response?.data ?? {}
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total) || 0,
    limit: Number(data.limit) || 50,
    offset: Number(data.offset) || 0,
    facets_included: data.facets_included !== false,
    counts: {
      by_status:
        data.counts?.by_status && typeof data.counts.by_status === 'object'
          ? data.counts.by_status
          : {},
      by_category:
        data.counts?.by_category && typeof data.counts.by_category === 'object'
          ? data.counts.by_category
          : {},
      by_publication_mode:
        data.counts?.by_publication_mode && typeof data.counts.by_publication_mode === 'object'
          ? data.counts.by_publication_mode
          : {},
    },
    category_facets: Array.isArray(data.category_facets)
      ? data.category_facets
          .filter((facet: any) => Number.isInteger(Number(facet?.id)) && Number(facet.id) > 0)
          .map((facet: any) => ({
            id: Number(facet.id),
            label: String(facet.label || facet.description || `Category ${facet.id}`),
            description: facet.description == null ? null : String(facet.description),
            count: Number(facet.count) || 0,
          }))
      : [],
    articles_matched: Array.isArray(data.articles_matched) ? data.articles_matched : undefined,
  }
}

export const pimMarketplaceCardsAPI = {
  async list(
    sellerId: number,
    params: PimMarketplaceCardsFilters,
    signal?: AbortSignal,
  ): Promise<PimMarketplaceCardsPage> {
    const response = await api.get(`/marketplace/sellers/${sellerId}/cards`, {
      params,
      signal,
    })
    return unwrap(response)
  },

  /**
   * Проверка списка артикулов на сервере: тот же запрос списка карточек с
   * articles_resolve=1 — сервер игнорирует прочие фильтры (кроме кабинета) и
   * возвращает подмножество переданных значений, для которых нашлась карточка.
   */
  async resolveArticles(sellerId: number, marketplace: PimMarketplace, values: string[]): Promise<string[]> {
    const response = await api.get(`/marketplace/sellers/${sellerId}/cards`, {
      params: { marketplace, articles: values.join(','), articles_resolve: '1', limit: 1 },
    })
    const data = response?.data?.data ?? response?.data ?? {}
    return Array.isArray(data?.articles_matched) ? data.articles_matched : []
  },

  async payloadPreview(
    sellerId: number,
    cardId: number,
    signal?: AbortSignal,
  ): Promise<PimMarketplacePayloadPreview> {
    const response = await api.get(
      `/marketplace/sellers/${sellerId}/cards/${cardId}/payload-preview`,
      { signal },
    )
    const data = response?.data?.data ?? response?.data ?? {}
    return {
      card_id: Number(data.card_id),
      article: String(data.article || ''),
      // Незнакомое значение — ошибка, а не молчаливый OZON (SPEC §3.1).
      marketplace: requireMarketplace(data.marketplace) as PimMarketplace,
      seller_id: Number(data.seller_id),
      exact: data.exact === true,
      source: data.source && typeof data.source === 'object' ? data.source : null,
      payload: data.payload && typeof data.payload === 'object' ? data.payload : null,
      media_urls: Array.isArray(data.media_urls)
        ? data.media_urls.filter((url: unknown): url is string => typeof url === 'string' && !!url)
        : [],
      validation_errors: Array.isArray(data.validation_errors)
        ? data.validation_errors.map((item: any) => ({
            code: String(item?.code || 'PAYLOAD_PREVIEW_FAILED'),
            message: String(item?.message || 'Карточка не может быть собрана'),
          }))
        : [],
      characteristic_labels:
        data.characteristic_labels && typeof data.characteristic_labels === 'object'
          ? data.characteristic_labels
          : {},
      characteristic_sources:
        data.characteristic_sources && typeof data.characteristic_sources === 'object'
          ? data.characteristic_sources
          : {},
      characteristic_sections:
        data.characteristic_sections && typeof data.characteristic_sections === 'object'
          ? data.characteristic_sections
          : {},
      category_name: typeof data.category_name === 'string' ? data.category_name : null,
      overrides: data.overrides && typeof data.overrides === 'object' ? data.overrides : null,
    }
  },

  /**
   * Сохранить ручные правки состава.
   *
   * Наружу ничего не уходит: бэкенд пишет новую ревизию карточки, из которой
   * потом соберётся тело отправки. Пустой объект снимает все правки.
   */
  async saveOverrides(
    sellerId: number,
    cardId: number,
    overrides: PimCardOverrides | Record<string, never>,
  ): Promise<{ revision: number; overrides: PimCardOverrides | null }> {
    const response = await api.post(`/marketplace/sellers/${sellerId}/cards/${cardId}/overrides`, {
      overrides,
    })
    const data = response?.data?.data ?? response?.data ?? {}
    const revision = Number(data.revision)
    if (!Number.isSafeInteger(revision) || revision <= 0) {
      throw new Error('Сервер не подтвердил новую revision карточки')
    }
    return {
      revision,
      overrides: data.overrides && typeof data.overrides === 'object' ? data.overrides : null,
    }
  },

  /** Постоянно запретить или снова разрешить выгрузку выбранных карточек. */
  async setPublishExclusion(
    cardIds: number[],
    excluded: boolean,
    reason?: string | null,
  ): Promise<{
    excluded: boolean
    results: Array<{
      card_id: number
      publish_excluded_at: string | null
      publish_excluded_by: number | null
      publish_excluded_reason: string | null
    }>
  }> {
    const response = await api.post('/cards/exclusion', {
      card_ids: cardIds,
      excluded,
      reason: reason ?? null,
    })
    const data = response?.data?.data ?? response?.data ?? {}
    return {
      excluded: data.excluded === true,
      results: Array.isArray(data.results)
        ? data.results.map((row: any) => ({
            card_id: Number(row.card_id),
            publish_excluded_at: row.publish_excluded_at == null
              ? null
              : String(row.publish_excluded_at),
            publish_excluded_by: row.publish_excluded_by == null
              ? null
              : Number(row.publish_excluded_by),
            publish_excluded_reason: row.publish_excluded_reason == null
              ? null
              : String(row.publish_excluded_reason),
          }))
        : [],
    }
  },

  /**
   * Безвозвратно удалить черновики карточек. Индивидуальные визуалы кабинета
   * этой карточки удаляются вместе с ней; общие слайды воронки и файлы печати
   * остаются — они принадлежат товару и макету, а не удаляемой карточке.
   *
   * Сервер отбраковывает часть id (уже выгружена, отправка в процессе, не
   * черновик, не найдена) — их разбор возвращается в `blocked`, вызывающий
   * код решает, как об этом сообщить.
   */
  async deleteDrafts(cardIds: number[]): Promise<PimMarketplaceDeleteResult> {
    const response = await api.post('/cards/delete', { card_ids: cardIds })
    const data = response?.data?.data ?? response?.data ?? {}
    return {
      deleted: Array.isArray(data.deleted) ? data.deleted.map((id: unknown) => Number(id)) : [],
      blocked: Array.isArray(data.blocked)
        ? data.blocked.map((row: any) => ({
            card_id: Number(row?.card_id),
            reason: (['published', 'in_flight', 'not_draft', 'not_found'] as const).includes(row?.reason)
              ? row.reason
              : 'not_found',
          }))
        : [],
      bundles_removed: Array.isArray(data.bundles_removed)
        ? data.bundles_removed.map((id: unknown) => Number(id))
        : [],
      revisions_removed: Number(data.revisions_removed) || 0,
      outbox_removed: Number(data.outbox_removed) || 0,
      visual_rows_removed: Number(data.visual_rows_removed) || 0,
      visual_files_deleted: Number(data.visual_files_deleted) || 0,
      warnings: Array.isArray(data.warnings) ? data.warnings.map((item: unknown) => String(item)) : [],
    }
  },
}
